import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * FeatureMockupCard
 * ─────────────────
 * A confident, understated feature card with a tiny live mini-mockup as the
 * visual instead of a generic icon. Designed to sell what lovinahome already
 * ships — not what it might ship.
 *
 * Props
 *   eyebrow      — small uppercase tag above the title ("Encrypted chat")
 *   title        — short, declarative ("Offers + viewings, in-thread")
 *   body         — one or two plain-English sentences
 *   bullets      — array of short proof bullets ("e-signature compliant", …)
 *   mockup       — small JSX rendered inside the framed preview area (~h-32)
 *   ctaLabel     — link text ("See it in chat")
 *   ctaTo        — react-router path
 *   accent       — Tailwind color token used for the small accent dot ("emerald", "purple", "amber", "rose", "cyan", "fuchsia", "indigo", "sky")
 *   firstBadge   — string label for the "First in …" pill (e.g. "Ireland", "the UK"). Falsy value hides the pill.
 *   testId       — data-testid for the root element
 */
const ACCENTS = {
  purple:  { dot: 'bg-[#7042cc]',    text: 'text-[#5b35a8]', ring: 'ring-[#7042cc]/15', tint: 'from-[#F5EFFF] to-white', soft: 'bg-[#F5EFFF]' },
  emerald: { dot: 'bg-emerald-500',  text: 'text-emerald-700', ring: 'ring-emerald-500/15', tint: 'from-emerald-50 to-white', soft: 'bg-emerald-50' },
  amber:   { dot: 'bg-amber-500',    text: 'text-amber-700', ring: 'ring-amber-500/15', tint: 'from-amber-50 to-white', soft: 'bg-amber-50' },
  rose:    { dot: 'bg-rose-500',     text: 'text-rose-700', ring: 'ring-rose-500/15', tint: 'from-rose-50 to-white', soft: 'bg-rose-50' },
  cyan:    { dot: 'bg-cyan-500',     text: 'text-cyan-700', ring: 'ring-cyan-500/15', tint: 'from-cyan-50 to-white', soft: 'bg-cyan-50' },
  fuchsia: { dot: 'bg-fuchsia-500',  text: 'text-fuchsia-700', ring: 'ring-fuchsia-500/15', tint: 'from-fuchsia-50 to-white', soft: 'bg-fuchsia-50' },
  indigo:  { dot: 'bg-indigo-500',   text: 'text-indigo-700', ring: 'ring-indigo-500/15', tint: 'from-indigo-50 to-white', soft: 'bg-indigo-50' },
  sky:     { dot: 'bg-sky-500',      text: 'text-sky-700', ring: 'ring-sky-500/15', tint: 'from-sky-50 to-white', soft: 'bg-sky-50' },
};

export const FeatureMockupCard = ({
  eyebrow,
  title,
  body,
  bullets = [],
  mockup,
  ctaLabel,
  ctaTo,
  accent = 'purple',
  firstBadge = '',
  testId,
}) => {
  const a = ACCENTS[accent] || ACCENTS.purple;
  return (
    <article
      data-testid={testId}
      className={`relative bg-white rounded-2xl ring-1 ${a.ring} hover:ring-2 hover:shadow-xl hover:shadow-black/[0.04] transition-all p-5 sm:p-6 flex flex-col`}
      style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
    >
      {/* mockup window */}
      <div
        className={`relative rounded-xl overflow-hidden ring-1 ring-black/5 bg-gradient-to-br ${a.tint} h-36 mb-5 flex items-center justify-center px-3`}
      >
        {mockup}
        {firstBadge && (
          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/85 text-white text-[10px] font-semibold tracking-wide">
            <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} />
            First in {firstBadge}
          </div>
        )}
      </div>

      {eyebrow && (
        <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${a.text} mb-1.5`}>
          <span className={`w-1.5 h-1.5 rounded-full ${a.dot}`} />
          {eyebrow}
        </div>
      )}
      <h3 className="text-[18px] sm:text-[19px] leading-snug text-gray-900 font-semibold mb-1.5 tracking-tight">
        {title}
      </h3>
      <p className="text-sm text-gray-600 leading-relaxed mb-3">{body}</p>

      {bullets.length > 0 && (
        <ul className="mt-auto pt-3 border-t border-gray-100 space-y-1.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-[12.5px] text-gray-700">
              <span className={`mt-1 inline-block w-1 h-1 rounded-full ${a.dot} shrink-0`} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      {ctaLabel && ctaTo && (
        <Link
          to={ctaTo}
          className={`mt-4 inline-flex items-center gap-1 text-sm font-semibold ${a.text} group/cta`}
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4 transition-transform group-hover/cta:translate-x-0.5" />
        </Link>
      )}
    </article>
  );
};

export default FeatureMockupCard;
